'use client';

import { motion } from 'framer-motion';
import type { ReactElement } from 'react';

interface Testimonial {
  avatar: string;
  name: string;
  handle: string;
  text: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    avatar: '/personas/diogo.png',
    name: 'Diogo',
    handle: 'Infoprodutor',
    text: 'Em uma semana o Facebook passou a entender melhor minhas vendas. Faturei mais gastando o mesmo.',
  },
  {
    avatar: '/personas/marina.png',
    name: 'Marina',
    handle: 'Gestora de Tráfego',
    text: 'Instalei em 10 minutos. Quando o cliente pede reembolso, o Trakd já avisa a Meta sozinho. Salvou meu mês.',
  },
];

/**
 * Painel direito das telas de auth (login + recuperar senha). Mesmo DNA do
 * squircle do ícone do brandbook §grad-bg-icon: base dark profunda + orbs
 * violeta/fúchsia + granulado. Escondido no mobile.
 */
export function AuthSplitHero(): ReactElement {
  return (
    <section className="relative hidden flex-1 p-4 md:block">
      <motion.div
        initial={{ opacity: 0, x: 40, filter: 'blur(8px)' }}
        animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className="bg-trakd-icon absolute inset-4 overflow-hidden rounded-3xl"
      >
        {/* Orbs ambient — luz que aparece dentro do ícone */}
        <div
          aria-hidden
          className="animate-orb-1 absolute -top-24 -right-24 h-[32rem] w-[32rem] rounded-full blur-3xl"
          style={{ background: 'rgba(144, 97, 255, 0.45)' }}
        />
        <div
          aria-hidden
          className="animate-orb-2 absolute -bottom-32 -left-20 h-[30rem] w-[30rem] rounded-full blur-3xl"
          style={{ background: 'rgba(159, 17, 206, 0.32)' }}
        />
        <div
          aria-hidden
          className="absolute top-1/3 left-1/2 h-[18rem] w-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-[50%] blur-3xl"
          style={{ background: 'rgba(166, 53, 149, 0.22)' }}
        />
        {/* Granulado denso (mesmo SVG noise do hero do ícone — brandbook L65) */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.22] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.92' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.87 0 0 0 0 0.86 0 0 0 0 0.96 0 0 0 0.55 0'/></filter><rect width='220' height='220' filter='url(%23n)' opacity='0.18'/></svg>\")",
          }}
        />
        <div className="relative z-10 flex h-full flex-col justify-end gap-12 p-10">
          <div className="max-w-md">
            <h2
              className="text-ink font-bold"
              style={{
                fontSize: 'clamp(36px, 4vw, 56px)',
                letterSpacing: '-0.035em',
                lineHeight: 1.02,
              }}
            >
              Tracking server-side,{' '}
              <span className="text-ink/80 font-light italic">sem fricção.</span>
            </h2>
            <p className="text-ink/70 mt-4 text-base leading-relaxed">
              CAPI nativa, integrações de checkout BR e cohort-aware. Conecte uma vez, escale com
              dados que a Meta confia.
            </p>
          </div>
          <div className="flex w-full items-stretch justify-center gap-4">
            <TestimonialCard t={TESTIMONIALS[0]!} delay={1.0} />
            <div className="hidden items-stretch xl:flex">
              <TestimonialCard t={TESTIMONIALS[1]!} delay={1.2} />
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

/**
 * Testimonial card no padrão `.persona-card` do brandbook (linhas 254-264).
 */
function TestimonialCard({ t, delay: d }: { t: Testimonial; delay: number }): ReactElement {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, filter: 'blur(6px)', scale: 0.96 }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)', scale: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: d }}
      className="flex h-full w-72 flex-col gap-4 rounded-2xl border border-white/15 bg-white/[0.06] p-6 backdrop-blur-xl"
    >
      <div className="flex items-center gap-3.5 border-b border-white/15 pb-4">
        <img
          src={t.avatar}
          alt={t.name}
          className="border-violet/50 h-12 w-12 flex-shrink-0 rounded-full border-[1.5px] object-cover shadow-[0_6px_20px_rgba(144,97,255,0.25)]"
        />
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-ink text-[15px] font-semibold tracking-tight">{t.name}</span>
          <span className="text-violet font-mono text-[10.5px] font-medium tracking-[0.08em] uppercase">
            {t.handle}
          </span>
        </div>
      </div>
      <p className="text-ink/80 text-[13.5px] leading-relaxed">{t.text}</p>
    </motion.div>
  );
}
